import { useMutation } from 'convex/react';
import { api } from '../../api';
import { useUserDevice } from './useUserDevice';
import { useSiteCategories } from './useSiteCategories';

export const useDeviceMutations = () => {
  const { device } = useUserDevice();
  const { blueJayRules: currentBlueJayRules } = useSiteCategories();
  
  const updateRestrictions = useMutation(api.devices.restrictions.update);
  const updatePolicies = useMutation(api.devices.policies.update);
  const updateApps = useMutation(api.devices.apps.update);
  
  const deviceId = device?._id;
  
  return {
    // Restrictions (General Restrictions page)
    // Payload: { [restrictionId]: boolean } where true = restrict, false = allow
    setRestriction: async (restrictionId: string, enabled: boolean) => {
      if (!deviceId) throw new Error('No device');
      
      console.log('[useDeviceMutations] setRestriction:', {
        deviceId,
        restrictionId,
        enabled,
        payload: { [restrictionId]: enabled }
      });
      
      try {
        await updateRestrictions({ 
          device: deviceId as any, 
          restrictions: { [restrictionId]: enabled } 
        });
        console.log('[useDeviceMutations] setRestriction SUCCESS');
      } catch (error) {
        console.error('[useDeviceMutations] setRestriction FAILED:', error);
        throw error;
      }
    },
    
    // Browser policies (blueJayRules)
    // IMPORTANT: Must rebuild entire blueJayRules object to preserve existing settings
    updateBrowserConfig: async (config: {
      categories?: string[];
      allowlist?: Record<string, boolean>;
      blocklist?: Record<string, boolean>;
      humanBlur?: boolean;
      nsfwLevel?: string;
    }) => {
      if (!deviceId) throw new Error('No device');
      
      console.log('[useDeviceMutations] updateBrowserConfig called:', {
        deviceId,
        inputConfig: config,
        currentBlueJayRules
      });
      
      // Convert allowlist/blocklist from objects to arrays
      const toArray = (input: Record<string, boolean> | string[] | undefined): string[] => {
        if (!input) return [];
        if (Array.isArray(input)) return input;
        return Object.keys(input);
      };
      
      // Get current blueJayRules and merge with updates
      const currentRules = currentBlueJayRules && typeof currentBlueJayRules === 'object' 
        ? currentBlueJayRules 
        : { categories: [], allowlist: [], blocklist: [], config: { nsfwLevel: 'medium', human: false, accuracy: 1 } };
      
      const blueJayRules = {
        categories: config.categories ?? (Array.isArray(currentRules.categories) ? currentRules.categories : []),
        allowlist: toArray(config.allowlist ?? currentRules.allowlist),
        blocklist: toArray(config.blocklist ?? currentRules.blocklist),
        config: {
          nsfwLevel: config.nsfwLevel ?? currentRules.config?.nsfwLevel ?? 'medium',
          human: config.humanBlur ?? currentRules.config?.human ?? false,
          accuracy: currentRules.config?.accuracy ?? 1,
        }
      };
      
      console.log('[useDeviceMutations] updateBrowserConfig payload:', {
        device: deviceId,
        blueJayRules
      });
      
      try {
        await updatePolicies({ 
          device: deviceId as any, 
          blueJayRules: blueJayRules as any
        });
        console.log('[useDeviceMutations] updateBrowserConfig SUCCESS');
      } catch (error) {
        console.error('[useDeviceMutations] updateBrowserConfig FAILED:', error);
        throw error;
      }
    },
    
    // Disable browser completely
    disableBrowser: async () => {
      if (!deviceId) throw new Error('No device');
      await updatePolicies({ 
        device: deviceId as any, 
        blueJayRules: false as any
      });
    },
    
    // App management - MUST send full state (allowedApps, blockedApps, appFilter) together
    updateAppSettings: async (settings: {
      allowedApps?: string[];
      blockedApps?: string[];
      appFilter?: Record<string, boolean>;
    }) => {
      if (!deviceId) throw new Error('No device');
      
      console.log('[useDeviceMutations] updateAppSettings called:', {
        deviceId,
        settings
      });
      
      try {
        await updateApps({ device: deviceId as any, ...settings } as any);
        console.log('[useDeviceMutations] updateAppSettings SUCCESS');
      } catch (error) {
        console.error('[useDeviceMutations] updateAppSettings FAILED:', error);
        throw error;
      }
    },
    
    hasDevice: !!deviceId,
  };
};
